/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


class SanCarousel extends HTMLElement {
    constructor() {
        super();
        this.state = { index: 0, isDragging: false, startX: 0, dragOffset: 0 };
        this.cardWidth = 0;
        
        // Bind methods once
        this.onResize = this.debounce(() => this.init(), 150);
        this.onDragStart = this.handleDrag.bind(this, 'start');
        this.onDragMove = this.handleDrag.bind(this, 'move');
        this.onDragEnd = this.handleDrag.bind(this, 'end');
    }

    connectedCallback() {
        this.wrapper = this.querySelector('.carousel-wrapper');
        this.dotsContainer = this.querySelector('.carousel-dots');
        this.cards = this.querySelectorAll('.carousel-card');
        this.desktopCards = parseInt(this.dataset.cardsPerView) || 3;
        
        if (!this.wrapper || !this.cards.length) return;
        
        this.init();
        this.bindEvents();
    }

    disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
    }

    debounce(fn, ms) {
        let id;
        return () => { clearTimeout(id); id = setTimeout(fn, ms); };
    }

    get itemsPerView() {
        const w = window.innerWidth;
        return w <= 768 ? 1 : w <= 1024 ? (this.desktopCards === 4 ? 3 : 2) : this.desktopCards;
    }

    get maxIndex() {
        return Math.max(0, this.cards.length - this.itemsPerView);
    }

    init() {
        // Cache card width
        const gap = parseFloat(getComputedStyle(this.wrapper).gap) || 30;
        this.cardWidth = this.cards[0].offsetWidth + gap;
        
        // Clamp index
        if (this.state.index > this.maxIndex) this.state.index = this.maxIndex;
        
        // Build dots with event delegation
        const fragment = document.createDocumentFragment();
        for (let i = 0; i <= this.maxIndex; i++) {
            const dot = document.createElement('div');
            dot.className = 'dot';
            dot.dataset.index = i;
            if (i === this.state.index) dot.classList.add('active');
            fragment.appendChild(dot);
        }
        this.dotsContainer.innerHTML = '';
        this.dotsContainer.appendChild(fragment);
        
        this.slide();
    }

    bindEvents() {
        // Navigation
        this.querySelector('.prev')?.addEventListener('click', () => this.go(-1));
        this.querySelector('.next')?.addEventListener('click', () => this.go(1));
        
        // Dots delegation
        this.dotsContainer.addEventListener('click', (e) => {
            if (e.target.classList.contains('dot')) {
                this.state.index = +e.target.dataset.index;
                this.slide();
            }
        });
        
        // Touch events for mobile
        this.wrapper.addEventListener('touchstart', this.onDragStart, { passive: false });
        this.wrapper.addEventListener('touchmove', this.onDragMove, { passive: false });
        this.wrapper.addEventListener('touchend', this.onDragEnd);
        this.wrapper.addEventListener('touchcancel', this.onDragEnd);
        
        // Mouse events for desktop
        this.wrapper.addEventListener('mousedown', this.onDragStart);
        this.wrapper.addEventListener('mousemove', this.onDragMove);
        this.wrapper.addEventListener('mouseup', this.onDragEnd);
        this.wrapper.addEventListener('mouseleave', this.onDragEnd);
        
        window.addEventListener('resize', this.onResize);
    }

    handleDrag(type, e) {
        const s = this.state;
        const isTouch = e.type.startsWith('touch');
        const clientX = isTouch ? e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX : e.clientX;
        
        if (type === 'start') {
            if (!isTouch) e.preventDefault();
            s.isDragging = true;
            s.startX = clientX;
            s.dragOffset = 0;
            this.wrapper.style.transition = 'none';
        } 
        else if (type === 'move' && s.isDragging) {
            s.dragOffset = clientX - s.startX;
            
            // Prevent vertical scroll only if horizontal swipe detected
            if (Math.abs(s.dragOffset) > 10) {
                e.preventDefault();
            }
            
            const tx = -s.index * this.cardWidth + s.dragOffset;
            this.wrapper.style.transform = `translateX(${tx}px)`;
        } 
        else if (type === 'end' && s.isDragging) {
            s.isDragging = false;
            this.wrapper.style.transition = '';
            
            if (Math.abs(s.dragOffset) > 50) {
                this.go(s.dragOffset < 0 ? 1 : -1);
            } else {
                this.slide();
            }
        }
    }

    go(dir) {
        const s = this.state;
        s.index = dir > 0 
            ? (s.index < this.maxIndex ? s.index + 1 : 0)
            : (s.index > 0 ? s.index - 1 : this.maxIndex);
        this.slide();
    }

    slide() {
        this.wrapper.style.transform = `translateX(${-this.state.index * this.cardWidth}px)`;
        this.dotsContainer.querySelectorAll('.dot').forEach((dot, i) => {
            dot.classList.toggle('active', i === this.state.index);
        });
    }
}
customElements.define('san-carousel', SanCarousel);
/**
 * San Video Section - Performance Optimized
 * Lazy loads video when section comes into viewport
 * Connection-aware: loads earlier on fast connections, later on slow ones
 * Safe to run on any page - won't error if section doesn't exist
 */
(function() {
  'use strict';

  // Check connection speed for smarter loading
  function getConnectionType() {
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return 'unknown';
    
    // Save-Data mode or slow connection
    if (connection.saveData) return 'slow';
    
    var type = connection.effectiveType;
    if (type === '4g') return 'fast';
    if (type === '3g') return 'medium';
    return 'slow'; // 2g or slow-2g
  }

  function initSanVideoSection(sectionId) {
    var wrapper = document.getElementById('san-video-wrapper-' + sectionId);
    var template = document.getElementById('san-video-template-' + sectionId);
    var media = document.getElementById('san-video-media-' + sectionId);

    if (!wrapper || !template || !media) return;

    var isLoaded = false;
    var connectionType = getConnectionType();
    
    // Adjust rootMargin based on connection speed
    // Fast: preload 300px before visible
    // Medium: preload 100px before visible  
    // Slow: only load when actually visible (0px)
    var rootMargin = connectionType === 'fast' ? '300px 0px' : 
                     connectionType === 'medium' ? '100px 0px' : '0px 0px';

    function loadVideo() {
      if (isLoaded) return;
      isLoaded = true;

      wrapper.classList.add('san-video-loading');

      var content = template.content.cloneNode(true);
      media.appendChild(content);

      var video = media.querySelector('video');
      if (video) {
        video.preload = 'auto';
        video.addEventListener('canplay', function() {
          wrapper.classList.remove('san-video-loading');
          wrapper.classList.add('san-video-loaded');
          video.play().catch(function() {});
        }, { once: true });
        
        // Fallback if canplay doesn't fire
        video.addEventListener('loadeddata', function() {
          wrapper.classList.remove('san-video-loading');
          wrapper.classList.add('san-video-loaded');
        }, { once: true });
      } else {
        // iframe (YouTube/Vimeo)
        wrapper.classList.remove('san-video-loading');
        wrapper.classList.add('san-video-loaded');
      }
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            // On slow connections, load immediately when visible
            // On fast connections, use idle callback
            if (connectionType === 'slow') {
              loadVideo();
            } else if ('requestIdleCallback' in window) {
              requestIdleCallback(loadVideo, { timeout: 500 });
            } else {
              setTimeout(loadVideo, 50);
            }
            observer.disconnect();
          }
        });
      }, {
        rootMargin: rootMargin,
        threshold: 0
      });

      observer.observe(wrapper);
    } else {
      // Fallback for old browsers - load after page loads
      if (document.readyState === 'complete') {
        loadVideo();
      } else {
        window.addEventListener('load', loadVideo, { once: true });
      }
    }
  }

  function initAllSanVideoSections() {
    var sections = document.querySelectorAll('.san-video-section');
    if (!sections.length) return;
    
    sections.forEach(function(section) {
      var sectionId = section.id ? section.id.replace('san-video-', '') : null;
      if (sectionId) {
        initSanVideoSection(sectionId);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSanVideoSections);
  } else {
    initAllSanVideoSections();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      var section = event.target.querySelector('.san-video-section');
      if (section) {
        var sectionId = section.id ? section.id.replace('san-video-', '') : null;
        if (sectionId) {
          initSanVideoSection(sectionId);
        }
      }
    });
  }
})();

(function () {
  'use strict';

  const PREFIX = 'Search for ';
  const PRODUCTS = ['Tawa', 'Kadai', 'Pan', 'Dutch oven'];
  const DRAWER_ID = 'search-drawer';

  const state = {
    idx: 0,
    char: 0,
    typing: false,
    deleting: false,
    timer: null,
    input: null,
    drawer: null,
    original: '',
    observer: null,
    initialized: false
  };

  const qs = (root, sel) => root && root.querySelector(sel);

  function getDrawer() {
    return state.drawer || (state.drawer = document.getElementById(DRAWER_ID));
  }

  function getInput() {
    const drawer = getDrawer();
    return drawer ? qs(drawer, '.predictive-search__input') : null;
  }

  function clearTimer() {
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }
  }

  function stopAnimation() {
    state.typing = false;
    clearTimer();
    if (state.input) state.input.placeholder = state.original;
  }

  function canAnimate() {
    return (
      state.input &&
      state.drawer &&
      state.drawer.hasAttribute('open') &&
      !state.input.value
    );
  }

  function startAnimation() {
    if (state.typing || !canAnimate()) return;
    state.typing = true;
    state.idx = state.char = 0;
    state.deleting = false;
    animate();
  }

  function animate() {
    if (!state.typing || !canAnimate()) {
      stopAnimation();
      return;
    }

    const product = PRODUCTS[state.idx];
    state.input.placeholder =
      PREFIX + product.substring(0, state.char) + '';

    if (!state.deleting) {
      if (state.char < product.length) {
        state.char++;
        state.timer = setTimeout(animate, 100);
      } else {
        state.deleting = true;
        state.timer = setTimeout(animate, 2000);
      }
    } else {
      if (state.char > 0) {
        state.char--;
        state.timer = setTimeout(animate, 50);
      } else {
        state.idx = (state.idx + 1) % PRODUCTS.length;
        state.deleting = false;
        state.timer = setTimeout(animate, 1000);
      }
    }
  }

  function bindInputEvents(input, drawer) {
    if (input._typedBound) return;
    input._typedBound = true;

    input.addEventListener('keydown', e => {
      if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete')
        stopAnimation();
    });

    input.addEventListener('input', e => {
      e.target.value
        ? stopAnimation()
        : drawer.hasAttribute('open') && setTimeout(startAnimation, 300);
    });

    input.addEventListener('focus', () => {
      if (!input.value && drawer.hasAttribute('open'))
        setTimeout(startAnimation, 100);
    });

    input.addEventListener('blur', () => {
      if (!input.value && drawer.hasAttribute('open'))
        setTimeout(startAnimation, 300);
    });
  }

  function observeDrawer(drawer) {
    if (state.observer) state.observer.disconnect();

    state.observer = new MutationObserver(muts => {
      muts.forEach(m => {
        if (m.attributeName === 'open') {
          drawer.hasAttribute('open')
            ? setTimeout(startAnimation, 500)
            : stopAnimation();
        }
      });
    });

    state.observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['open']
    });
  }

  function init() {
    const drawer = getDrawer();
    if (!drawer) return;

    const input = getInput();
    if (!input) return;

    if (state.input === input && state.initialized) return;

    state.drawer = drawer;
    state.input = input;
    state.original = input.placeholder || '';
    state.initialized = true;

    bindInputEvents(input, drawer);
    observeDrawer(drawer);
  }

  function tryInit() {
    [0, 300, 800, 1500].forEach(t => setTimeout(init, t));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInit);
  } else {
    tryInit();
  }

  new MutationObserver(init).observe(document.body, {
    childList: true,
    subtree: true
  });

  document.addEventListener(
    'click',
    e => {
      if (e.target.closest('[aria-controls="' + DRAWER_ID + '"]')) {
        setTimeout(startAnimation, 800);
      }
    },
    true
  );

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () =>
      setTimeout(init, 300)
    );
  }
})();
class TextSlide extends HTMLElement {
  #currentIndex = 1;
  #timer = null;
  #observer = null;
  #isTransitioning = false;
  #itemCount = 0;

  connectedCallback() {
    const children = Array.from(this.children);
    if (children.length <= 1) return;
    this.#itemCount = children.length;
    this.interval = parseInt(this.getAttribute('interval')) || 2000;
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('wrapper');
    const items = children.map(child => {
      const div = document.createElement('div');
      div.classList.add('item');
      div.appendChild(child);
      return div;
    });

    this.wrapper.appendChild(items.at(-1).cloneNode(true));
    items.forEach(item => this.wrapper.appendChild(item));
    this.wrapper.appendChild(items[0].cloneNode(true));

    this.appendChild(this.wrapper);

    this.#setPosition(false);
    this.#attachTransitionEnd();
    this.#attachVisibilityPause();
    this.#attachIntersectionPause();
  }
  #setPosition(animate = true) {
    this.wrapper.style.transition = animate ? 'transform 0.5s ease' : 'none';
    this.wrapper.style.transform = `translateX(-${this.#currentIndex * 100}%)`;
  }
  #jumpTo(index) {
    this.#currentIndex = index;
    this.wrapper.style.transition = 'none';
    this.wrapper.style.transform = `translateX(-${index * 100}%)`;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.#isTransitioning = false;
    }));
  }

  #startSlide() {
    this.#timer = setInterval(() => {
      if (this.#isTransitioning) return;
      this.#isTransitioning = true;
      this.#currentIndex++;
      this.#setPosition(true);
    }, this.interval);
  }

  #stopSlide() {
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #attachTransitionEnd() {
    const totalItems = this.#itemCount + 2;

    this.wrapper.addEventListener('transitionend', () => {
      if (this.#currentIndex === totalItems - 1) {
        this.#jumpTo(1);
      } else if (this.#currentIndex === 0) {
        this.#jumpTo(this.#itemCount);
      } else {
        this.#isTransitioning = false;
      }
    });
  }
  #attachVisibilityPause() {
    this._onVisibility = () => {
      if (document.hidden) {
        this.#stopSlide();
      } else {
        this.#startSlide();
      }
    };
    document.addEventListener('visibilitychange', this._onVisibility);
    this.#startSlide();
  }
  #attachIntersectionPause() {
    this.#observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!this.#timer) this.#startSlide();
      } else {
        this.#stopSlide();
      }
    }, { threshold: 0 });

    this.#observer.observe(this);
  }
  disconnectedCallback() {
    this.#stopSlide();
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this._onVisibility);
  }
}
customElements.define('text-slide', TextSlide);


(function () {
  'use strict';

  /* ── CONFIG ──────────────────────────────────────────────────────── */
  const CFG = {
    DATA_URL:        'https://cdn.shopify.com/s/files/1/0717/2653/0775/files/updated_pincodes_format.json?v=1779198835',
    UNAVAIL_MSG:     "Sorry, we do not ship to this pincode. Please try another one.",
    DISPATCH_CUTOFF: 15,
  };

  const DAYS_RANGE_MAP = { '2': [2, 3], '3': [3, 4], '4': [4, 6], '5': [5, 7] };

  function getDeliveryDates(days) {
    const daysStr = String(days).trim();

    const now    = new Date();
    const cutoff = new Date();
    cutoff.setHours(CFG.DISPATCH_CUTOFF, 0, 0, 0);

    const fmt = d => d.getDate() + ' ' + d.toLocaleString('en-IN', { month: 'long' });

    // Special case: 1-day delivery → "Tomorrow" if before cutoff, else 2 days out
    if (daysStr === '1') {
      if (now < cutoff) return 'Tomorrow';
      const target = new Date(now);
      target.setDate(target.getDate() + 2);
      return fmt(target);
    }

    const range = DAYS_RANGE_MAP[daysStr];
    if (!range) return String(days);

    // If past cutoff, dispatch starts tomorrow — shift base by 1
    const base = new Date(now);
    if (now >= cutoff) base.setDate(base.getDate() + 1);

    const start = new Date(base);
    start.setDate(start.getDate() + range[0]);

    const end = new Date(base);
    end.setDate(end.getDate() + range[1]);

    return fmt(start) + ' – ' + fmt(end);
  }

  /* ── Shared data state (one fetch, shared across all instances) ───── */
  let pincodeData   = null;
  let loadState     = 'idle';
  let loadCallbacks = [];

  function loadData(cb) {
    if (loadState === 'done' || loadState === 'error') { cb?.(); return; }
    if (cb) loadCallbacks.push(cb);
    if (loadState === 'loading') return;

    loadState = 'loading';
    fetch(CFG.DATA_URL)
      .then(r => r.json())
      .then(data => { pincodeData = data; loadState = 'done'; })
      .catch(err => {
        console.warn('[sandeep-edd] Failed to load pincode data', err);
        pincodeData = {};
        loadState   = 'error';
      })
      .finally(() => {
        const cbs = loadCallbacks.splice(0);
        cbs.forEach(fn => fn());
      });
  }

  class SandeepEdd extends HTMLElement {

    connectedCallback() {
      this._timer = null;
      this._cacheRefs();
      this._bindEvents();
      this._schedulePreload();
    }

    disconnectedCallback() {
      clearInterval(this._timer);
    }

    _cacheRefs() {
      const q = s => this.querySelector(s);
      this._input      = q('.pc-input');
      this._btn        = q('.pc-btn');
      this._resultBox  = q('.pc-result');
      this._titleEl    = q('.pc-result-title');
      this._subEl      = q('.pc-result-sub');
      this._changeEl   = q('.pc-change');
      this._checkedPin = q('.pc-change strong');

      // Hijack the onclick="pcReset()" on the <a> — no HTML edit needed
      this._changeLink = q('.pc-change a');
      if (this._changeLink) {
        this._changeLink.removeAttribute('onclick');
        this._changeLink.addEventListener('click', e => {
          e.preventDefault();
          this._reset();
        });
      }
    }

    _bindEvents() {
      this._input.addEventListener('input', () => {
        const val = this._input.value.replace(/\D/g, '').slice(0, 6);
        if (this._input.value !== val) this._input.value = val;
        this._btn.disabled = val.length !== 6;
        this._hideResult();
      });

      this._input.addEventListener('keydown', e => {
        if (e.key === 'Enter' && !this._btn.disabled) this._doCheck();
      });

      this._btn.addEventListener('click', () => this._doCheck());
    }

    _schedulePreload() {
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          this._requestIdleOrLoad();
        }, { rootMargin: '200px' });
        io.observe(this);
      } else {
        setTimeout(() => this._requestIdleOrLoad(), 1500);
      }
    }

    _requestIdleOrLoad() {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => loadData(), { timeout: 2000 });
      } else {
        setTimeout(() => loadData(), 200);
      }
    }

    _doCheck() {
      const pin = this._input.value.trim();
      if (pin.length !== 6) return;

      this._btn.classList.add('pc-checking');
      this._btn.disabled = true;
      this._hideResult();

      loadData(() => this._showResult(pin));
    }

    _showResult(pin) {
      this._btn.classList.remove('pc-checking');

      let info = null;
      const cacheKey = 'pc_' + pin;
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) info = JSON.parse(cached);
      } catch (_) {}

      if (!info && pincodeData) {
        info = pincodeData[pin] || null;
        try { sessionStorage.setItem(cacheKey, JSON.stringify(info)); } catch (_) {}
      }

      const available = info && info.available !== false;

      if (available) {
        const dateRange = getDeliveryDates(info.days);
        this._subEl.innerHTML =
          'Free Delivery by <strong>' + dateRange + '</strong>'
          + '<span class="pc-dispatch text--xxsmall" id="pc-dispatch-note-' + pin + '"></span>';

        this._resultBox.classList.remove('pc-unavail');
        this._resultBox.classList.add('pc-show', 'pc-success');
        this._startCountdown('pc-dispatch-note-' + pin);
      } else {
        this._subEl.textContent = CFG.UNAVAIL_MSG;
        this._resultBox.classList.remove('pc-success');
        this._resultBox.classList.add('pc-show', 'pc-unavail');
      }

      this._checkedPin.textContent = pin;
      this._changeEl.classList.add('pc-show');
      this._input.disabled    = true;
      this._btn.style.display = 'none';
    }

    _startCountdown(noteId) {
      clearInterval(this._timer);

      const tick = () => {
        const el = document.getElementById(noteId);
        if (!el) { clearInterval(this._timer); return; }

        const now    = new Date();
        const cutoff = new Date();
        cutoff.setHours(CFG.DISPATCH_CUTOFF, 0, 0, 0);

        if (now >= cutoff) {
          // el.textContent = '⏰ Order now — dispatches tomorrow morning.';
          clearInterval(this._timer);
          return;
        }

        const diff = cutoff - now;
        const h = Math.floor(diff / 3_600_000);
        const m = Math.floor((diff % 3_600_000) / 60_000);
        const s = Math.floor((diff % 60_000) / 1_000);
        const hPart = h > 0 ? h + 'h ' : '';
        el.innerHTML = '⚡ Order within <strong>'
          + hPart
          + String(m).padStart(2, '0') + 'm '
          + String(s).padStart(2, '0') + 's'
          + '</strong> for same-day dispatch!';
      };

      tick();
      this._timer = setInterval(tick, 1000);
    }

    _hideResult() {
      clearInterval(this._timer);
      this._resultBox.classList.remove('pc-show', 'pc-success', 'pc-unavail');
      this._changeEl.classList.remove('pc-show');
    }

    _reset() {
      clearInterval(this._timer);
      this._timer             = null;
      this._input.value       = '';
      this._input.disabled    = false;
      this._btn.style.display = '';
      this._btn.disabled      = true;
      this._hideResult();
      this._input.focus();
    }
  }

  customElements.define('sandeep-edd', SandeepEdd);

})();

class ProductOptionSelector extends HTMLElement {
  connectedCallback() {
    this._isAdding = false;
    this._activeIndex = -1;

    this._cards = Array.from(this.querySelectorAll('.pos__card'));
    this._headingValue = this.querySelector('[data-pos-heading-value]');

    this._variantsMap = this._cards.map(card => {
      const script = card.querySelector('script[data-pos-variants]');
      return script ? JSON.parse(script.textContent) : [];
    });

    const optScript = this.querySelector('script[data-pos-options]');
    this._optionsMap = optScript ? JSON.parse(optScript.textContent) : [];

    this._selectedOptions = [];
    this._cards.forEach((card, i) => {
      const opts = {};
      card.querySelectorAll('.pos__swatch--active').forEach(sw => {
        opts[sw.dataset.optionName] = sw.dataset.optionValue;
      });
      this._selectedOptions[i] = opts;
    });

    this.addEventListener('click', this._onClick.bind(this));
  }

  _onClick(e) {
    const atcBtn = e.target.closest('[data-pos-atc]');
    if (atcBtn) { this._addToCart(); return; }

    const swatch = e.target.closest('.pos__swatch');
    if (swatch) { this._onSwatchClick(swatch); return; }

    const card = e.target.closest('.pos__card');
    if (card) {
      const index = parseInt(card.dataset.posIndex);
      if (!isNaN(index)) this._selectCard(index);
    }
  }

  _selectCard(index) {
    if (index === this._activeIndex) {
      this._activeIndex = -1;
      this._cards[index].classList.remove('pos__card--active');
      if (this._headingValue) this._headingValue.textContent = '';
      return;
    }

    this._activeIndex = index;

    this._cards.forEach((card, i) => {
      card.classList.toggle('pos__card--active', i === index);
    });

    if (this._headingValue) {
      const title = this._cards[index].querySelector('.pos__card-title');
      if (title) this._headingValue.textContent = title.textContent;
    }
  }

  _onSwatchClick(swatch) {
    const card = swatch.closest('.pos__card');
    const index = parseInt(card.dataset.posIndex);
    const optName = swatch.dataset.optionName;
    const optValue = swatch.dataset.optionValue;

    this._selectedOptions[index][optName] = optValue;

    const group = swatch.closest('.pos__option-group');
    group.querySelectorAll('.pos__swatch').forEach(sw => {
      sw.classList.toggle('pos__swatch--active', sw.dataset.optionValue === optValue);
    });

    const label = group.querySelector('[data-pos-option-value]');
    if (label) label.textContent = optValue;

    this._updateVariant(index);
  }

  _getVariant(index) {
    const variants = this._variantsMap[index];
    const options = this._optionsMap[index];
    const selected = this._selectedOptions[index];
    if (!variants || !options) return null;

    return variants.find(v =>
      options.every((optName, i) => v.options[i] === selected[optName])
    ) || null;
  }

  _formatMoney(cents) {
    if (typeof formatMoney === 'function') return formatMoney(cents);
    return (cents / 100).toLocaleString('en-IN', {
      style: 'currency', currency: 'INR', minimumFractionDigits: 0
    });
  }

  _updateVariant(index) {
    const variant = this._getVariant(index);
    const card = this._cards[index];
    if (!variant || !card) return;

    const img = card.querySelector('[data-pos-image]');
    if (img && variant.featured_image) {
      img.src = variant.featured_image;
    }

    const priceEl = card.querySelector('[data-pos-price]');
    if (priceEl) priceEl.innerHTML = this._formatMoney(variant.price);

    const compareEl = card.querySelector('[data-pos-compare-price]');
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.innerHTML = this._formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    const atcBtn = card.querySelector('[data-pos-atc]');
    if (atcBtn) {
      atcBtn.dataset.variantId = variant.id;
      atcBtn.disabled = !variant.available;
      atcBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }
  }

  async _addToCart() {
    if (this._isAdding) return;

    const card = this._cards[this._activeIndex];
    const atcBtn = card?.querySelector('[data-pos-atc]');
    const variantId = atcBtn?.dataset.variantId;
    if (!variantId) return;

    this._isAdding = true;

    if (atcBtn) {
      atcBtn.disabled = true;
      atcBtn.setAttribute('aria-busy', 'true');
      atcBtn.textContent = 'Adding...';
    }

    try {
      const addResponse = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1,
          sections: ['mini-cart']
        })
      });

      const responseJson = await addResponse.json();

      if (atcBtn) atcBtn.removeAttribute('aria-busy');

      if (addResponse.ok) {
        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        this.dispatchEvent(new CustomEvent('variant:added', {
          bubbles: true,
          detail: {
            variant: responseJson.hasOwnProperty('items') ? responseJson['items'][0] : responseJson
          }
        }));

        const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
        const cartContent = await cartResponse.json();

        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartContent }
        }));

        cartContent['sections'] = responseJson['sections'];

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: window.themeVariables.settings.cartType === 'drawer'
          }
        }));

        if (atcBtn) {
          atcBtn.textContent = 'Added!';
          setTimeout(() => {
            atcBtn.disabled = false;
            atcBtn.textContent = 'Add to Cart';
          }, 1500);
        }
      } else {
        this.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (atcBtn) {
          atcBtn.disabled = false;
          atcBtn.textContent = 'Add to Cart';
        }
      }

      this.dispatchEvent(new CustomEvent('cart-notification:show', {
        bubbles: true,
        cancelable: true,
        detail: {
          status: addResponse.ok ? 'success' : 'error',
          error: responseJson['description'] || ''
        }
      }));

    } catch (err) {
      console.error('[ProductOptionSelector]', err);
      if (atcBtn) {
        atcBtn.removeAttribute('aria-busy');
        atcBtn.disabled = false;
        atcBtn.textContent = 'Add to Cart';
      }
    } finally {
      this._isAdding = false;
    }
  }
}

customElements.define('product-option-selector', ProductOptionSelector);